import { useCallback } from 'react'

import { toast } from '@inno-ui/toast'
import { useQueryClient } from '@tanstack/react-query'
import {
  normalizeDisplayOrder,
  selectDesktopWidgets,
  setDesktopWidgetsQueryData,
  transformWidgetsForSave
} from 'modules/desktop/modules/widgets/utils'
import {
  buildWidgetOrderSavePayload,
  canPersistWidgetOrder
} from 'modules/desktop/modules/widgets/utils/buildWidgetOrderSavePayload'
import {
  enqueueWidgetWrite,
  getWidgetWriteQueue
} from 'modules/desktop/modules/widgets/utils/widgetWriteQueue'
import { useTranslation } from 'react-i18next'
import {
  GET_ALL_DESKTOP_SETTINGS_QUERY_KEY,
  type GetAllDesktopSettingsData
} from 'shared/api/containerApi'
import {
  useDeleteWidgetMutation,
  useSaveWidgetsMutation
} from 'shared/api/containerApi/requests'
import { WIDGET_NAMES } from 'shared/constants/widgets'
import { logger } from 'shared/services/logger'
import { widgetSingletonKey } from 'shared/utils/dedupeWidgets'

export const UNDO_WIDGET_REMOVAL_WINDOW_MS = 5_000

/**
 * Removes from the cache immediately. DELETE and undo share the widget write
 * queue with order saves, so an older POST cannot recreate a removed widget.
 */
export const useRemoveWidget = () => {
  const queryClient = useQueryClient()
  const deleteWidgetMutation = useDeleteWidgetMutation()
  const saveWidgetsMutation = useSaveWidgetsMutation()
  const { t } = useTranslation('translation', { keyPrefix: 'widgets' })

  return useCallback(
    (id: string) => {
      const previousData = queryClient.getQueryData<GetAllDesktopSettingsData>([
        GET_ALL_DESKTOP_SETTINGS_QUERY_KEY
      ])
      const items = selectDesktopWidgets(previousData)
      const index = items.findIndex(item => item.id === id)
      const item = items[index]
      if (!item) return

      let undoRequested = false
      const queue = getWidgetWriteQueue(queryClient)
      queue.revision += 1
      const revision = queue.revision
      queue.latestWidgets = normalizeDisplayOrder(
        items.filter(widget => widget.id !== id)
      )
      setDesktopWidgetsQueryData(queryClient, queue.latestWidgets)

      const deletionPromise = enqueueWidgetWrite(queryClient, async () => {
        await queryClient.cancelQueries({
          queryKey: [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
        })
        setDesktopWidgetsQueryData(queryClient, queue.latestWidgets)
        try {
          await deleteWidgetMutation.mutateAsync(id)
        } catch (error) {
          // A later queued save must reconcile if this deletion did not land.
          // eslint-disable-next-line require-atomic-updates
          queue.needsReconcile = true
          if (revision === queue.revision && previousData) {
            queryClient.setQueryData(
              [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY],
              previousData
            )
          }
          throw error
        } finally {
          if (revision === queue.revision) {
            queue.needsReconcile = false
            await queryClient.invalidateQueries({
              queryKey: [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
            })
          }
        }
      })

      void deletionPromise
        .then(() => {
          if (!undoRequested) {
            logger.track('widget.removed', { widgetKey: item.widgetKey })
          }
        })
        .catch(() => {
          if (undoRequested) return
          logger.track('widget.remove_failed', { widgetKey: item.widgetKey })
          toast.error({
            title: t('remove_failed'),
            subTitle: t('remove_failed_message')
          })
        })

      const restore = () => {
        if (undoRequested) return
        undoRequested = true

        const currentData = queryClient.getQueryData<GetAllDesktopSettingsData>(
          [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
        )
        const currentItems = selectDesktopWidgets(currentData)
        const singletonKey = widgetSingletonKey(item)
        const duplicateExists = currentItems.some(
          currentItem =>
            currentItem.id !== item.id &&
            widgetSingletonKey(currentItem) === singletonKey
        )

        if (duplicateExists) return

        const restoredItems = [...currentItems]
        const safeIndex = Math.max(0, Math.min(index, restoredItems.length))
        restoredItems.splice(
          canPersistWidgetOrder(currentData) ? safeIndex : restoredItems.length,
          0,
          item
        )
        const normalizedItems = normalizeDisplayOrder(restoredItems)
        queue.revision += 1
        const restoreRevision = queue.revision
        queue.latestWidgets = normalizedItems
        setDesktopWidgetsQueryData(queryClient, normalizedItems)
        logger.track('widget.remove_undone', { widgetKey: item.widgetKey })

        void enqueueWidgetWrite(queryClient, async () => {
          try {
            await deletionPromise
          } catch {
            // Undo already restored the optimistic cache. DELETE did not land.
            logger.track('widget.undo_delete_failed', {
              widgetKey: item.widgetKey
            })
            return
          }

          const latestData =
            queryClient.getQueryData<GetAllDesktopSettingsData>([
              GET_ALL_DESKTOP_SETTINGS_QUERY_KEY
            ])
          if (
            !selectDesktopWidgets(latestData).some(widget => widget.id === id)
          ) {
            return
          }

          try {
            await saveWidgetsMutation.mutateAsync({
              widgets:
                canPersistWidgetOrder(latestData) && latestData
                  ? buildWidgetOrderSavePayload(
                      latestData,
                      selectDesktopWidgets(latestData)
                    )
                  : transformWidgetsForSave([item])
            })
            if (restoreRevision === queue.revision) {
              queue.needsReconcile = false
              await queryClient.invalidateQueries({
                queryKey: [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
              })
            }
          } catch {
            if (restoreRevision === queue.revision) {
              queue.needsReconcile = false
              void queryClient.invalidateQueries({
                queryKey: [GET_ALL_DESKTOP_SETTINGS_QUERY_KEY]
              })
            }
            logger.track('widget.restore_failed', {
              widgetKey: item.widgetKey
            })
            toast.error({
              title: t('restore_failed'),
              subTitle: t('restore_failed_message')
            })
          }
        }).catch(() => {})
      }

      toast.info({
        title: t('widget_removed', {
          name: WIDGET_NAMES[item.widgetKey]
        }),
        onUndo: restore,
        options: { autoClose: UNDO_WIDGET_REMOVAL_WINDOW_MS }
      })
    },
    [deleteWidgetMutation, queryClient, saveWidgetsMutation, t]
  )
}
